import ControlViewModel from '@/ViewModels/ControlViewModel';
import FilterViewModel from '@/ViewModels/Controls/FilterViewModel';
import DropDownListViewModelT from '@/ViewModels/Controls/DropDownListViewModel';
import CheckBoxFactoryT from '@/Factories/ControlFactories/CheckBoxFactory';
import ComponentManager from '@/Management/ComponentManager';
import ResourceManager from '@/Management/ResourceManager';
import GenericObject from '@/Model/GenericObject';
import Utilities from '@/Utility/Utilities';
import ControlFactory from '@/Factories/ControlFactory';

type ConnectorValue = 'AND' | 'OR';
type Connector = { name: string, value: ConnectorValue };

/**
 * Das ViewModel für die FilterElemente
 */
export default class FilterElementViewModel extends ControlViewModel {
    private DropDownListViewModel = ComponentManager.getViewModel<typeof DropDownListViewModelT>('DropDownListViewModel');

    /**
     * Das ViewModel des Filters
     */
    public filter: FilterViewModel;

    /**
     * Die derzeitige Factory
     */
    public filterFactory: KnockoutObservable<ControlFactory>;

    /**
     * Der Wert
     */
    public value: KnockoutObservable<any>;

    /**
     * Gibt den Index des aktuellen Filterelements im Filter an
     */
    public index: KnockoutComputed<number>;

    /**
     * Gibt die auswählbaren Connectoren in der aktuellen Sprache an
     */
    private connectors: Array<Connector>;

    /**
     * Die Column
     */
    public column: KnockoutObservable<any>;

    /**
     * Das GenericObject zur Anbindung an das Control
     */
    public genericObject: GenericObject;

    /**
     * Das ViewModel für die Spaltenauswahl
     */
    public dropDownListColumns: DropDownListViewModelT;

    /**
     * Der Operator
     */
    public operator: KnockoutObservable<any>;

    /**
     * Das ViewModel für die Operatorenauswahl
     */
    private operatorsData: KnockoutObservableArray<any>;

    public dropDownListOperators: DropDownListViewModelT;

    /**
     * Der Connector
     */
    public connector: KnockoutObservable<ConnectorValue>;

    /**
     * Das ViewModel für die Connectorauswahl
     */
    public dropDownListConnectors: DropDownListViewModelT;
    // Dieses ViewModel wird auch als context in DetailViewItems weitergegeben, daher muss das Interface stimmen

    public defaultIterators: {};

    public isComplete: KnockoutComputed<boolean>;
    private refreshIsComplete: KnockoutObservable<boolean>; // handle to refresh this.isComplete-computed
    private checkCompleteSubscriptions: Array<KnockoutSubscription>;

    private onceHadAColumn: boolean = false;

    /**
     * @param {object} params - Ein Objekt mit Optionen für das Control
     * @constructor
     */
    constructor(params) {
        super(params);

        this.filter = this.paramOrDefault(params.filter, null);
        this.filterFactory = this.observableOrDefault(params.factory, null);
        this.value = this.observableOrDefault(params.value, null);

        this.index = this.defineComputed(() => {
            return this.filter.elements.indexOf(this);
        });

        this.defineSubscription(this.index,
            (value) => {
                this.locator(value + '');
            });

        this.connectors = [
            { name: ResourceManager.getInstance().get('AND'), value: 'AND' as ConnectorValue },
            { name: ResourceManager.getInstance().get('OR'), value: 'OR' as ConnectorValue }
        ];

        this.column = this.observableOrDefault(params.column, null);
        this.onceHadAColumn = !!params.column;

        this.genericObject = (() => {
            let values = {};
            let column = this.column();
            let value = this.value();

            if (column != null) {
                if (this.isComplexValue(value)) {
                    _.each(value, (value, key) => {
                        values[key] = value;
                    });
                }
                else {
                    column = column.replace('.ObjectIdentValue', '').replace('_ObjectIdentValue', '').replace('.MLSValue', '');
                    values[column] = value;
                }
            }

            return new GenericObject({
                pdoId: '0',
                values: values
            });
        })();

        this.dropDownListColumns = new this.DropDownListViewModel({
            parentLocator: this.locator,
            locator: 'columns',
            value: this.column,
            data: this.defineComputed(() => {
                let sorted = [];
                let columns = this.filter.columns();

                for (let i = 0; i < columns.length; i++) {
                    let column = jQuery.extend(true, {}, columns[i], { locator: columns[i].name });
                    let index = _.sortedIndex(sorted, column, 'sortableTitle');

                    if (column.filterable) {
                        sorted.splice(index, 0, column);
                    }
                }

                return sorted;
            }),
            height: 500,
            isAutoWidth: false,
            dataTextField: 'title',
            dataValueField: 'field',
            optionLabel: ' ',
            isEnabled: this.defineComputed(() => {
                let elements = this.filter.elements();
                let index = elements.indexOf(this);

                if (index == 0)
                    return true;

                let prevElement = elements[index - 1];
                if (!Utilities.isNullOrUndefined(prevElement))
                    return Utilities.isNotBlank(prevElement.column());
                else
                    return false;
            })
        });

        // adds or removes filter elements depending on the filter-column-selection
        this.defineSubscription(this.dropDownListColumns.value, (value) => {
            this.operator(null);
            this.filter.rearrange();
        });

        this.operator = this.observableOrDefault(params.operator, null);
        this.operatorsData = this.observableOrDefault([]) as KnockoutObservableArray<any>;

        this.dropDownListOperators = new this.DropDownListViewModel({
            parentLocator: this.locator,
            locator: 'operators',
            dataTextField: 'name',
            dataValueField: 'ergName',
            optionLabel: ' ',
            isEnabled: this.defineComputed(() => {
                return this.operatorsData().length > 0;
            }),
            value: this.operator,
            data: this.operatorsData
        });


        this.connector = this.observableOrDefault(params.connector, this.connectors[0].value);

        this.dropDownListConnectors = new this.DropDownListViewModel({
            parentLocator: this.locator,
            locator: 'connectors',
            value: this.connector,
            data: this.connectors,
            isEnabled: this.defineComputed(() => {
                let currentIndex = this.index();
                return currentIndex < this.filter.elements().length - 1;
            }),
            dataTextField: 'name',
            dataValueField: 'value'
        });

        // defines the isComplete-state of this filterElement
        this.refreshIsComplete = this.observableOrDefault(true);
        this.isComplete = this.defineComputed(() => {
            let column = this.getColumn();
            let factory = this.filterFactory();
            let isFactoryValid = !Utilities.isNullOrUndefined(factory) ? factory.isValid() : false;
            let operator = this.getOperator();
            let refreshed = this.refreshIsComplete();

            return ko.ignoreDependencies(() => {
                const value = (factory && !factory.isDisposed) ? factory.getFilterInfo().value : '';
                const isInvalid = Utilities.isBlank(column) || Utilities.isBlank(operator) || !isFactoryValid
                    || (!(factory instanceof CheckBoxFactoryT) && Utilities.isBlank(value));

                return !isInvalid;
            });
        });

        this.checkCompleteSubscriptions = [];
        this.defineSubscription(this.filterFactory, factory => {
            this.checkCompleteSubscriptions.forEach(s => s.dispose());
            this.checkCompleteSubscriptions = [];

            Object.keys(this.genericObject.values).forEach(key => {
                this.checkCompleteSubscriptions.push(this.genericObject.values[key].subscribe(
                    obsValue => this.refreshIsComplete.valueHasMutated()
                ));
            });
        });

        this.defaultIterators = {};
        this.undisposeProperties.push("filter");

        ControlViewModel.checkProperties(this, params);
    }

    public getFilterExpression(withConnector: boolean): string {
        let column = this.getColumn();
        let operator = this.getOperator();
        let connector = this.getConnector();
        let value = this.genericObject.getValue(this.getColumnName());

        let ret = '"' + column + '" ' + operator + ' "' + value + '"';

        if (withConnector) {
            ret += " " + connector;
        }

        return ret;
    }

    private getConnector() {
        let columnObj = _.findWhere(this.connectors, { value: this.connector() });
        if (columnObj) {
            return columnObj.name.toLowerCase();
        }
        return null;
    }

    private getColumn() {
        let columnObj = _.findWhere(this.filter.columns(), { field: this.column() });
        if (columnObj) {
            return columnObj.title;
        }
        return null;
    }

    private getColumnName() {
        let columnObj = _.findWhere(this.filter.columns(), { field: this.column() });
        if (columnObj) {
            return columnObj.name;
        }
        return null;
    }

    private getOperator() {
        let op = _.findWhere(this.operatorsData(), { ergName: this.operator() });
        if (op) {
            return op.name.toLowerCase();
        }
        return null;
    }

    private isComplexValue(value) {
        if (value && typeof value === "object") {
            return true;
        }

        return false;
    }

    // Dieses ViewModel wird auch als context in DetailViewItems weitergegeben, daher muss das Interface stimmen
    public notifyBeforeUpdatedAssociation() {
        return true;
    }

    public getNestedContextPath() {
        return '';
    }

    /**
     * @override 
     **/
    protected disposeInternal(): void {
        this.checkCompleteSubscriptions.forEach(s => s.dispose);
        super.disposeInternal();
    }
}

ComponentManager.setViewModel('FilterElementViewModel', FilterElementViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');